import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Briefcase, Plus, Send, ToggleLeft, ToggleRight, Pencil, Save, X, Mail, Upload, Search,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/lib/auth";
import { HeldLinkNotice } from "@/components/HeldLinkNotice";
import { useViewAsClient } from "@/lib/viewAsClient";
import {
  listApprovedBookers,
  createApprovedBooker,
  updateApprovedBooker,
  deactivateApprovedBooker,
  inviteApprovedBookerLogin,
} from "@/lib/api";

const empty = {
  name: "",
  abn: "",
  contact_email: "",
  contact_phone: "",
  notes: "",
  is_active: true,
  auto_approve_bookings: false,
};

export default function ApprovedBookers() {
  const { profile, roles } = useAuth();
  const { viewAsClientId } = useViewAsClient();
  const isSuper = roles.includes("super_admin");
  const myClientId = profile?.client_id || null;
  // For super admins we honour their View-As selection (may be null = all)
  const scopeClientId = isSuper ? viewAsClientId : myClientId;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive | imported
  const [search, setSearch] = useState("");
  const includeInactive = true;
  const [editing, setEditing] = useState(null);     // booker row being edited or "new"
  const [draft, setDraft] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [inviteFor, setInviteFor] = useState(null); // booker row → invite dialog
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [heldLink, setHeldLink] = useState(null);

  const fetchRows = async () => {
    setLoading(true);
    const { data, error } = await listApprovedBookers({
      clientId: scopeClientId || undefined,
      includeInactive,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message || "Failed to load Approved Bookers");
      return;
    }
    setRows(data || []);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await listApprovedBookers({
        clientId: scopeClientId || undefined,
        includeInactive,
      });
      if (cancelled) return;
      setLoading(false);
      if (error) {
        toast.error(error.message || "Failed to load Approved Bookers");
        return;
      }
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [scopeClientId, includeInactive]);

  const openNew = () => {
    if (!scopeClientId) {
      toast.error("Pick a tenant (View-As) before adding an Approved Booker.");
      return;
    }
    setDraft({ ...empty });
    setEditing("new");
  };

  const openEdit = (row) => {
    setDraft({
      name: row.name || "",
      abn: row.abn || "",
      contact_email: row.contact_email || "",
      contact_phone: row.contact_phone || "",
      notes: row.notes || "",
      is_active: !!row.is_active,
      auto_approve_bookings: !!row.auto_approve_bookings,
    });
    setEditing(row);
  };

  const save = async () => {
    if (!draft.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!draft.contact_email?.trim()) {
      toast.error("Contact email is required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        const { data, error } = await createApprovedBooker({
          ...draft,
          client_id: scopeClientId,
        });
        if (error) throw error;
        toast.success(`Created "${data.name}"`);
      } else {
        const { error } = await updateApprovedBooker(editing.id, draft);
        if (error) throw error;
        toast.success("Saved");
      }
      setEditing(null);
      fetchRows();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    if (row.is_active) {
      const { error } = await deactivateApprovedBooker(row.id);
      if (error) toast.error(error.message);
      else toast.success("Deactivated");
    } else {
      const { error } = await updateApprovedBooker(row.id, { ...row, is_active: true });
      if (error) toast.error(error.message);
      else toast.success("Reactivated");
    }
    fetchRows();
  };

  const openInvite = (row) => {
    setInviteFor(row);
    setHeldLink(null);
    setInviteEmail(row.contact_email || "");
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await inviteApprovedBookerLogin({
        bookerId: inviteFor.id,
        contactEmail: inviteEmail.trim(),
      });
      if (res.held) {
        setHeldLink(res.action_link || "");
        toast.warning("Email hold is on — no email sent");
      } else {
        toast.success(`Invite sent to ${res.email}`);
        setInviteFor(null);
      }
    } catch (e) {
      toast.error(e.message || "Invite failed");
    } finally {
      setInviting(false);
    }
  };

  const isImported = (r) => (r.notes || "").startsWith("Imported from legacy");
  const counts = useMemo(() => ({
    all: rows.length,
    active: rows.filter((r) => r.is_active).length,
    inactive: rows.filter((r) => !r.is_active).length,
    imported: rows.filter(isImported).length,
  }), [rows]);
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter === "active" && !r.is_active) return false;
      if (statusFilter === "inactive" && r.is_active) return false;
      if (statusFilter === "imported" && !isImported(r)) return false;
      if (!q) return true;
      return [r.name, r.contact_email, r.contact_phone, r.abn, r.notes]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [rows, statusFilter, search]);

  const tenantHint = useMemo(() => {
    if (isSuper && !scopeClientId) return "Showing all tenants — use View-As to filter / add.";
    return null;
  }, [isSuper, scopeClientId]);

  return (
    <div className="space-y-6" data-testid="approved-bookers-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Briefcase className="h-6 w-6" /> Approved Bookers
          </h1>
          <p className="text-sm text-slate-500">
            Third parties (transport companies, brokers, agents) authorised to nominate drivers and book time slots for your tenant.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild data-testid="import-bookers-btn">
            <Link to="/admin/import/approved-bookers"><Upload className="h-4 w-4 mr-1.5" /> Import CSV</Link>
          </Button>
          <Button onClick={openNew} data-testid="new-booker-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Approved Booker
          </Button>
        </div>
      </div>

      {tenantHint && (
        <div className="rounded-md border bg-amber-50 px-3 py-2 text-xs text-amber-800">{tenantHint}</div>
      )}

      <Card>
        <CardHeader className="py-3 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-sm">
              Approved Bookers <span className="text-slate-400 font-normal" data-testid="bookers-visible-count">({visibleRows.length} shown)</span>
            </CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, phone, notes…"
                className="pl-8 h-9"
                data-testid="bookers-search"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="bookers-status-filter">
            {[
              ["all", "All"], ["active", "Active"], ["inactive", "Inactive"], ["imported", "Imported from legacy"],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setStatusFilter(k)}
                data-testid={`bookers-filter-${k}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  statusFilter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label} <span className="opacity-70" data-testid={`bookers-count-${k}`}>{counts[k]}</span>
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>ABN</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Auto-approve</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">Loading…</TableCell></TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">{rows.length ? "No bookers match this filter." : "No Approved Bookers yet."}</TableCell></TableRow>
              ) : visibleRows.map((row) => (
                <TableRow key={row.id} data-testid={`booker-row-${row.id}`}>
                  <TableCell className="max-w-[320px]">
                    <div className="font-medium flex items-center gap-1.5">
                      {row.name}
                      {isImported(row) && <Badge variant="outline" className="text-[10px] text-blue-700 border-blue-200">legacy</Badge>}
                    </div>
                    {row.notes && <div className="text-xs text-slate-400 truncate" title={row.notes}>{row.notes}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{row.contact_email}</div>
                    {row.contact_phone && <div className="text-xs text-slate-500">{row.contact_phone}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{row.abn || "—"}</TableCell>
                  <TableCell>
                    {row.is_active
                      ? <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">active</Badge>
                      : <Badge variant="outline" className="text-slate-400">inactive</Badge>}
                  </TableCell>
                  <TableCell>
                    {row.auto_approve_bookings
                      ? <Badge className="bg-amber-100 text-amber-700" data-testid={`auto-approve-on-${row.id}`}>yes</Badge>
                      : <Badge variant="outline" className="text-slate-400">no</Badge>}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openInvite(row)} data-testid={`invite-${row.id}`}>
                      <Send className="h-3.5 w-3.5 mr-1" /> Invite login
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(row)} data-testid={`edit-${row.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(row)} data-testid={`toggle-${row.id}`}>
                      {row.is_active ? <ToggleRight className="h-4 w-4 text-emerald-500" /> : <ToggleLeft className="h-4 w-4 text-slate-400" />}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add Approved Booker" : "Edit Approved Booker"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Name *">
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="booker-name" />
            </Field>
            <Field label="Contact email *">
              <Input type="email" value={draft.contact_email} onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })} data-testid="booker-email" />
            </Field>
            <Field label="Contact phone">
              <Input value={draft.contact_phone} onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })} data-testid="booker-phone" />
            </Field>
            <Field label="ABN">
              <Input value={draft.abn} onChange={(e) => setDraft({ ...draft, abn: e.target.value })} data-testid="booker-abn" />
            </Field>
            <Field label="Notes (internal)">
              <Textarea rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="booker-notes" />
            </Field>
            <div className="flex items-start gap-3 rounded-md border bg-amber-50 px-3 py-2">
              <input
                id="auto-approve"
                type="checkbox"
                checked={!!draft.auto_approve_bookings}
                onChange={(e) => setDraft({ ...draft, auto_approve_bookings: e.target.checked })}
                data-testid="booker-auto-approve"
                className="mt-0.5"
              />
              <label htmlFor="auto-approve" className="text-sm cursor-pointer">
                <strong>Auto-approve bookings from this booker</strong>
                <div className="text-xs text-amber-700">
                  Skip the <code>pending_approval</code> step. Use only for trusted carriers you don&apos;t need to vet per booking.
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}><X className="h-4 w-4 mr-1.5" /> Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="booker-save">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite dialog */}
      <Dialog open={inviteFor !== null} onOpenChange={(o) => !o && setInviteFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite login for {inviteFor?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Sends a Supabase password-set email and a branded invite. The recipient becomes an
            <strong> Approved Booker user </strong> scoped to this booker&apos;s tenant.
          </p>
          <Field label="Email to invite">
            <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email" />
          </Field>
          {heldLink !== null && <HeldLinkNotice link={heldLink} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteFor(null)}><X className="h-4 w-4 mr-1.5" /> Cancel</Button>
            <Button onClick={sendInvite} disabled={inviting || !inviteEmail.trim() || heldLink !== null} data-testid="invite-send">
              <Mail className="h-4 w-4 mr-1.5" /> {inviting ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
